// Icon resolution for the UI. Shared icon textures (iconKey.*, weapon art) are
// built by the art modules; when one is missing the UI generates a clean white
// pictogram under a private key ('uifb:<key>') so screens never show holes.
// White pictograms are meant to be tinted; isMonochrome() tells whether a
// shared texture can be tinted without muddying its colours.

import type Phaser from 'phaser';

const SIZE = 64;
const monoCache = new Map<string, boolean>();

type Ctx = CanvasRenderingContext2D;
type Painter = (c: Ctx, s: number) => void;

/** Resolve a texture key: the shared one if it exists, else a generated fallback pictogram. */
export function iconTexture(scene: Phaser.Scene, key: string): string {
  if (scene.textures.exists(key)) return key;
  const fb = `uifb:${key}`;
  if (scene.textures.exists(fb)) return fb;
  const painter = painterFor(key);
  const wide = key.startsWith('weapon_');
  const w = wide ? SIZE * 2 : SIZE;
  const tex = scene.textures.createCanvas(fb, w, SIZE);
  if (!tex) return '__DEFAULT';
  const c = tex.getContext();
  c.clearRect(0, 0, w, SIZE);
  c.fillStyle = '#ffffff';
  c.strokeStyle = '#ffffff';
  c.lineJoin = 'round';
  c.lineCap = 'round';
  if (wide) {
    c.save();
    painter(c, SIZE);
    c.restore();
  } else {
    painter(c, SIZE);
  }
  tex.refresh();
  monoCache.set(fb, true);
  return fb;
}

/** True if the texture is (nearly) greyscale — i.e. tinting it with a colour gives that colour. */
export function isMonochrome(scene: Phaser.Scene, key: string): boolean {
  const cached = monoCache.get(key);
  if (cached !== undefined) return cached;
  let mono = false;
  try {
    const frame = scene.textures.getFrame(key);
    const w = frame?.width ?? 0;
    const h = frame?.height ?? 0;
    let samples = 0;
    let saturated = 0;
    for (let iy = 1; iy < 8; iy++) {
      for (let ix = 1; ix < 8; ix++) {
        const px = scene.textures.getPixel(Math.floor((w * ix) / 8), Math.floor((h * iy) / 8), key);
        if (!px || px.alpha < 40) continue;
        samples++;
        const max = Math.max(px.red, px.green, px.blue);
        const min = Math.min(px.red, px.green, px.blue);
        if (max - min > 40) saturated++;
      }
    }
    mono = samples > 0 && saturated / samples < 0.15;
  } catch {
    mono = false;
  }
  monoCache.set(key, mono);
  return mono;
}

/** Tint to apply to an icon so it reads in `color` (white pictograms only). */
export function iconTint(scene: Phaser.Scene, key: string, color: number): number {
  return isMonochrome(scene, key) ? color : 0xffffff;
}

// ───────────────────────────── Pictograms ─────────────────────────────

function poly(c: Ctx, pts: number[][], s: number, fill = true): void {
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x * s, y * s) : c.moveTo(x * s, y * s)));
  c.closePath();
  if (fill) c.fill();
  else c.stroke();
}

function ring(c: Ctx, x: number, y: number, r: number, w: number, s: number): void {
  c.lineWidth = w * s;
  c.beginPath();
  c.arc(x * s, y * s, r * s, 0, Math.PI * 2);
  c.stroke();
}

function dot(c: Ctx, x: number, y: number, r: number, s: number): void {
  c.beginPath();
  c.arc(x * s, y * s, r * s, 0, Math.PI * 2);
  c.fill();
}

const bolt: Painter = (c, s) => poly(c, [[0.58, 0.06], [0.24, 0.54], [0.46, 0.54], [0.38, 0.94], [0.78, 0.4], [0.54, 0.4], [0.66, 0.06]], s);

const flame: Painter = (c, s) => {
  c.beginPath();
  c.moveTo(0.5 * s, 0.06 * s);
  c.bezierCurveTo(0.62 * s, 0.3 * s, 0.86 * s, 0.42 * s, 0.82 * s, 0.66 * s);
  c.bezierCurveTo(0.78 * s, 0.88 * s, 0.62 * s, 0.95 * s, 0.5 * s, 0.95 * s);
  c.bezierCurveTo(0.34 * s, 0.95 * s, 0.18 * s, 0.86 * s, 0.18 * s, 0.64 * s);
  c.bezierCurveTo(0.18 * s, 0.46 * s, 0.32 * s, 0.4 * s, 0.36 * s, 0.24 * s);
  c.bezierCurveTo(0.44 * s, 0.34 * s, 0.46 * s, 0.42 * s, 0.44 * s, 0.5 * s);
  c.bezierCurveTo(0.54 * s, 0.4 * s, 0.56 * s, 0.24 * s, 0.5 * s, 0.06 * s);
  c.fill();
  c.globalCompositeOperation = 'destination-out';
  c.beginPath();
  c.moveTo(0.5 * s, 0.56 * s);
  c.bezierCurveTo(0.62 * s, 0.68 * s, 0.64 * s, 0.84 * s, 0.5 * s, 0.86 * s);
  c.bezierCurveTo(0.38 * s, 0.86 * s, 0.36 * s, 0.72 * s, 0.5 * s, 0.56 * s);
  c.fill();
  c.globalCompositeOperation = 'source-over';
};

const chevrons: Painter = (c, s) => {
  poly(c, [[0.12, 0.18], [0.34, 0.18], [0.6, 0.5], [0.34, 0.82], [0.12, 0.82], [0.38, 0.5]], s);
  poly(c, [[0.44, 0.18], [0.66, 0.18], [0.92, 0.5], [0.66, 0.82], [0.44, 0.82], [0.7, 0.5]], s);
};

const shield: Painter = (c, s) => {
  c.beginPath();
  c.moveTo(0.5 * s, 0.06 * s);
  c.lineTo(0.86 * s, 0.2 * s);
  c.lineTo(0.84 * s, 0.5 * s);
  c.quadraticCurveTo(0.8 * s, 0.8 * s, 0.5 * s, 0.95 * s);
  c.quadraticCurveTo(0.2 * s, 0.8 * s, 0.16 * s, 0.5 * s);
  c.lineTo(0.14 * s, 0.2 * s);
  c.closePath();
  c.fill();
  c.globalCompositeOperation = 'destination-out';
  poly(c, [[0.5, 0.2], [0.72, 0.29], [0.7, 0.5], [0.5, 0.78], [0.5, 0.2]], s);
  c.globalCompositeOperation = 'source-over';
};

const starburst: Painter = (c, s) => {
  const pts: number[][] = [];
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? 0.46 : 0.22;
    pts.push([0.5 + Math.cos(a) * r, 0.5 + Math.sin(a) * r]);
  }
  poly(c, pts, s);
};

const vortex: Painter = (c, s) => {
  c.lineWidth = 0.08 * s;
  for (let i = 0; i < 3; i++) {
    c.beginPath();
    const r = 0.16 + i * 0.13;
    c.arc(0.5 * s, 0.5 * s, r * s, i * 2.1, i * 2.1 + Math.PI * 1.35);
    c.stroke();
  }
  dot(c, 0.5, 0.5, 0.08, s);
};

const bullet: Painter = (c, s) => {
  c.beginPath();
  c.moveTo(0.36 * s, 0.9 * s);
  c.lineTo(0.36 * s, 0.4 * s);
  c.quadraticCurveTo(0.36 * s, 0.12 * s, 0.5 * s, 0.06 * s);
  c.quadraticCurveTo(0.64 * s, 0.12 * s, 0.64 * s, 0.4 * s);
  c.lineTo(0.64 * s, 0.9 * s);
  c.closePath();
  c.fill();
  c.globalCompositeOperation = 'destination-out';
  c.fillRect(0.3 * s, 0.68 * s, 0.4 * s, 0.05 * s);
  c.globalCompositeOperation = 'source-over';
};

const crosshair: Painter = (c, s) => {
  ring(c, 0.5, 0.5, 0.3, 0.07, s);
  c.lineWidth = 0.07 * s;
  for (const [x0, y0, x1, y1] of [[0.5, 0.04, 0.5, 0.3], [0.5, 0.7, 0.5, 0.96], [0.04, 0.5, 0.3, 0.5], [0.7, 0.5, 0.96, 0.5]]) {
    c.beginPath();
    c.moveTo(x0 * s, y0 * s);
    c.lineTo(x1 * s, y1 * s);
    c.stroke();
  }
  dot(c, 0.5, 0.5, 0.06, s);
};

const heatWaves: Painter = (c, s) => {
  c.lineWidth = 0.08 * s;
  for (const x of [0.3, 0.5, 0.7]) {
    c.beginPath();
    c.moveTo(x * s, 0.9 * s);
    c.bezierCurveTo((x - 0.12) * s, 0.7 * s, (x + 0.12) * s, 0.5 * s, x * s, 0.32 * s);
    c.bezierCurveTo((x - 0.1) * s, 0.22 * s, (x + 0.04) * s, 0.14 * s, x * s, 0.08 * s);
    c.stroke();
  }
};

const rocket: Painter = (c, s) => {
  c.save();
  c.translate(0.5 * s, 0.5 * s);
  c.rotate(-Math.PI / 4);
  c.beginPath();
  c.moveTo(0, -0.44 * s);
  c.quadraticCurveTo(0.14 * s, -0.28 * s, 0.13 * s, 0.18 * s);
  c.lineTo(-0.13 * s, 0.18 * s);
  c.quadraticCurveTo(-0.14 * s, -0.28 * s, 0, -0.44 * s);
  c.fill();
  poly(c, [[0.13, 0.04], [0.27, 0.3], [0.13, 0.24]], s);
  poly(c, [[-0.13, 0.04], [-0.27, 0.3], [-0.13, 0.24]], s);
  c.globalAlpha = 0.75;
  poly(c, [[-0.08, 0.22], [0.08, 0.22], [0, 0.44]], s);
  c.restore();
};

const chainLinks: Painter = (c, s) => {
  c.lineWidth = 0.09 * s;
  for (const [x, y] of [[0.36, 0.38], [0.64, 0.62]]) {
    c.save();
    c.translate(x * s, y * s);
    c.rotate(-Math.PI / 4);
    c.beginPath();
    c.ellipse(0, 0, 0.24 * s, 0.13 * s, 0, 0, Math.PI * 2);
    c.stroke();
    c.restore();
  }
};

const plus: Painter = (c, s) => {
  c.fillRect(0.38 * s, 0.12 * s, 0.24 * s, 0.76 * s);
  c.fillRect(0.12 * s, 0.38 * s, 0.76 * s, 0.24 * s);
};

const swords: Painter = (c, s) => {
  for (const dir of [1, -1]) {
    c.save();
    c.translate(0.5 * s, 0.5 * s);
    c.rotate((dir * Math.PI) / 4);
    poly(c, [[-0.05, -0.44], [0.05, -0.44], [0.05, 0.18], [-0.05, 0.18]], s);
    c.fillRect(-0.16 * s, 0.18 * s, 0.32 * s, 0.07 * s);
    c.fillRect(-0.04 * s, 0.25 * s, 0.08 * s, 0.18 * s);
    c.restore();
  }
};

const horned: Painter = (c, s) => {
  poly(c, [[0.5, 0.2], [0.82, 0.5], [0.5, 0.92], [0.18, 0.5]], s);
  poly(c, [[0.24, 0.42], [0.08, 0.06], [0.4, 0.3]], s);
  poly(c, [[0.76, 0.42], [0.92, 0.06], [0.6, 0.3]], s);
  c.globalCompositeOperation = 'destination-out';
  poly(c, [[0.36, 0.52], [0.46, 0.5], [0.44, 0.58]], s);
  poly(c, [[0.64, 0.52], [0.54, 0.5], [0.56, 0.58]], s);
  c.globalCompositeOperation = 'source-over';
};

const gem: Painter = (c, s) => {
  poly(c, [[0.3, 0.14], [0.7, 0.14], [0.9, 0.38], [0.5, 0.92], [0.1, 0.38]], s);
  c.globalCompositeOperation = 'destination-out';
  c.lineWidth = 0.035 * s;
  c.beginPath();
  c.moveTo(0.1 * s, 0.38 * s);
  c.lineTo(0.9 * s, 0.38 * s);
  c.moveTo(0.36 * s, 0.38 * s);
  c.lineTo(0.5 * s, 0.9 * s);
  c.lineTo(0.64 * s, 0.38 * s);
  c.stroke();
  c.globalCompositeOperation = 'source-over';
};

const cog: Painter = (c, s) => {
  const pts: number[][] = [];
  for (let i = 0; i < 32; i++) {
    const a = (i / 32) * Math.PI * 2;
    const r = Math.floor(i / 2) % 2 === 0 ? 0.46 : 0.36;
    pts.push([0.5 + Math.cos(a) * r, 0.5 + Math.sin(a) * r]);
  }
  poly(c, pts, s);
  c.globalCompositeOperation = 'destination-out';
  dot(c, 0.5, 0.5, 0.16, s);
  c.globalCompositeOperation = 'source-over';
};

const skull: Painter = (c, s) => {
  c.beginPath();
  c.arc(0.5 * s, 0.42 * s, 0.34 * s, Math.PI * 0.85, Math.PI * 2.15);
  c.lineTo(0.7 * s, 0.86 * s);
  c.lineTo(0.3 * s, 0.86 * s);
  c.closePath();
  c.fill();
  c.globalCompositeOperation = 'destination-out';
  poly(c, [[0.26, 0.42], [0.46, 0.46], [0.42, 0.58], [0.3, 0.56]], s);
  poly(c, [[0.74, 0.42], [0.54, 0.46], [0.58, 0.58], [0.7, 0.56]], s);
  poly(c, [[0.5, 0.6], [0.55, 0.7], [0.45, 0.7]], s);
  c.fillRect(0.4 * s, 0.78 * s, 0.03 * s, 0.1 * s);
  c.fillRect(0.485 * s, 0.78 * s, 0.03 * s, 0.1 * s);
  c.fillRect(0.57 * s, 0.78 * s, 0.03 * s, 0.1 * s);
  c.globalCompositeOperation = 'source-over';
};

const startRing: Painter = (c, s) => {
  ring(c, 0.5, 0.5, 0.34, 0.09, s);
  dot(c, 0.5, 0.5, 0.14, s);
};

const hexNut: Painter = (c, s) => {
  const pts: number[][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    pts.push([0.5 + Math.cos(a) * 0.44, 0.5 + Math.sin(a) * 0.44]);
  }
  poly(c, pts, s);
  c.globalCompositeOperation = 'destination-out';
  dot(c, 0.5, 0.5, 0.17, s);
  c.globalCompositeOperation = 'source-over';
};

const crystal: Painter = (c, s) => {
  poly(c, [[0.5, 0.04], [0.8, 0.3], [0.8, 0.7], [0.5, 0.96], [0.2, 0.7], [0.2, 0.3]], s);
  c.globalCompositeOperation = 'destination-out';
  c.lineWidth = 0.04 * s;
  c.beginPath();
  c.moveTo(0.5 * s, 0.04 * s);
  c.lineTo(0.5 * s, 0.96 * s);
  c.moveTo(0.2 * s, 0.3 * s);
  c.lineTo(0.5 * s, 0.5 * s);
  c.lineTo(0.8 * s, 0.3 * s);
  c.stroke();
  c.globalCompositeOperation = 'source-over';
};

const padlock: Painter = (c, s) => {
  c.lineWidth = 0.1 * s;
  c.beginPath();
  c.arc(0.5 * s, 0.38 * s, 0.18 * s, Math.PI, 0);
  c.lineTo(0.68 * s, 0.48 * s);
  c.moveTo(0.32 * s, 0.48 * s);
  c.lineTo(0.32 * s, 0.38 * s);
  c.stroke();
  c.fillRect(0.2 * s, 0.46 * s, 0.6 * s, 0.44 * s);
  c.globalCompositeOperation = 'destination-out';
  dot(c, 0.5, 0.63, 0.06, s);
  c.fillRect(0.47 * s, 0.63 * s, 0.06 * s, 0.14 * s);
  c.globalCompositeOperation = 'source-over';
};

const upChevrons: Painter = (c, s) => {
  poly(c, [[0.5, 0.08], [0.88, 0.46], [0.72, 0.46], [0.5, 0.26], [0.28, 0.46], [0.12, 0.46]], s);
  poly(c, [[0.5, 0.44], [0.88, 0.82], [0.72, 0.82], [0.5, 0.62], [0.28, 0.82], [0.12, 0.82]], s);
};

const chip: Painter = (c, s) => {
  c.fillRect(0.24 * s, 0.24 * s, 0.52 * s, 0.52 * s);
  for (let i = 0; i < 3; i++) {
    const o = 0.32 + i * 0.15;
    c.fillRect((o - 0.03) * s, 0.08 * s, 0.06 * s, 0.14 * s);
    c.fillRect((o - 0.03) * s, 0.78 * s, 0.06 * s, 0.14 * s);
    c.fillRect(0.08 * s, (o - 0.03) * s, 0.14 * s, 0.06 * s);
    c.fillRect(0.78 * s, (o - 0.03) * s, 0.14 * s, 0.06 * s);
  }
  c.globalCompositeOperation = 'destination-out';
  c.fillRect(0.34 * s, 0.34 * s, 0.32 * s, 0.32 * s);
  c.globalCompositeOperation = 'source-over';
  c.fillRect(0.42 * s, 0.42 * s, 0.16 * s, 0.16 * s);
};

const helix: Painter = (c, s) => {
  c.lineWidth = 0.08 * s;
  for (const phase of [0, Math.PI]) {
    c.beginPath();
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      const x = 0.5 + Math.sin(t * Math.PI * 2 + phase) * 0.28;
      const y = 0.08 + t * 0.84;
      if (i) c.lineTo(x * s, y * s);
      else c.moveTo(x * s, y * s);
    }
    c.stroke();
  }
  c.lineWidth = 0.05 * s;
  for (let i = 1; i < 6; i++) {
    const t = i / 6;
    const x0 = 0.5 + Math.sin(t * Math.PI * 2) * 0.28;
    const x1 = 0.5 + Math.sin(t * Math.PI * 2 + Math.PI) * 0.28;
    const y = 0.08 + t * 0.84;
    c.beginPath();
    c.moveTo(x0 * s, y * s);
    c.lineTo(x1 * s, y * s);
    c.stroke();
  }
};

const chestPlate: Painter = (c, s) => {
  poly(c, [[0.2, 0.14], [0.4, 0.1], [0.5, 0.2], [0.6, 0.1], [0.8, 0.14], [0.88, 0.5], [0.66, 0.9], [0.34, 0.9], [0.12, 0.5]], s);
  c.globalCompositeOperation = 'destination-out';
  c.lineWidth = 0.05 * s;
  c.beginPath();
  c.moveTo(0.5 * s, 0.28 * s);
  c.lineTo(0.5 * s, 0.86 * s);
  c.moveTo(0.22 * s, 0.5 * s);
  c.lineTo(0.78 * s, 0.5 * s);
  c.stroke();
  c.globalCompositeOperation = 'source-over';
};

const pauldron: Painter = (c, s) => {
  c.beginPath();
  c.moveTo(0.1 * s, 0.7 * s);
  c.quadraticCurveTo(0.12 * s, 0.16 * s, 0.6 * s, 0.14 * s);
  c.quadraticCurveTo(0.9 * s, 0.16 * s, 0.9 * s, 0.44 * s);
  c.lineTo(0.62 * s, 0.5 * s);
  c.lineTo(0.5 * s, 0.86 * s);
  c.closePath();
  c.fill();
  c.globalCompositeOperation = 'destination-out';
  c.fillRect(0.3 * s, 0.34 * s, 0.4 * s, 0.05 * s);
  c.globalCompositeOperation = 'source-over';
};

const reactor: Painter = (c, s) => {
  c.fillRect(0.22 * s, 0.1 * s, 0.56 * s, 0.8 * s);
  c.globalCompositeOperation = 'destination-out';
  dot(c, 0.5, 0.42, 0.18, s);
  c.fillRect(0.3 * s, 0.7 * s, 0.4 * s, 0.05 * s);
  c.globalCompositeOperation = 'source-over';
  dot(c, 0.5, 0.42, 0.1, s);
};

const leg: Painter = (c, s) => {
  poly(c, [[0.34, 0.06], [0.62, 0.06], [0.6, 0.42], [0.66, 0.5], [0.62, 0.74], [0.9, 0.82], [0.9, 0.94], [0.3, 0.94], [0.34, 0.74], [0.3, 0.5], [0.36, 0.42]], s);
  c.globalCompositeOperation = 'destination-out';
  c.fillRect(0.3 * s, 0.45 * s, 0.4 * s, 0.04 * s);
  c.globalCompositeOperation = 'source-over';
};

const gun: Painter = (c, s) => {
  poly(c, [[0.06, 0.38], [0.74, 0.38], [0.74, 0.32], [0.94, 0.32], [0.94, 0.46], [0.74, 0.46], [0.74, 0.54], [0.4, 0.54], [0.34, 0.84], [0.2, 0.84], [0.24, 0.54], [0.06, 0.54]], s);
};

const crescent: Painter = (c, s) => {
  dot(c, 0.5, 0.5, 0.4, s);
  c.globalCompositeOperation = 'destination-out';
  dot(c, 0.64, 0.4, 0.34, s);
  c.globalCompositeOperation = 'source-over';
};

const artillery: Painter = (c, s) => {
  for (const x of [0.22, 0.5, 0.78]) {
    poly(c, [[x - 0.08, 0.1], [x + 0.08, 0.1], [x + 0.08, 0.5], [x + 0.16, 0.5], [x, 0.74], [x - 0.16, 0.5], [x - 0.08, 0.5]], s);
  }
  c.fillRect(0.08 * s, 0.84 * s, 0.84 * s, 0.07 * s);
};

const echo: Painter = (c, s) => {
  c.globalAlpha = 0.45;
  poly(c, [[0.22, 0.2], [0.5, 0.5], [0.22, 0.8], [0.1, 0.5]], s);
  c.globalAlpha = 0.7;
  poly(c, [[0.42, 0.2], [0.7, 0.5], [0.42, 0.8], [0.3, 0.5]], s);
  c.globalAlpha = 1;
  poly(c, [[0.62, 0.16], [0.94, 0.5], [0.62, 0.84], [0.5, 0.5]], s);
};

const hexWall: Painter = (c, s) => {
  c.lineWidth = 0.06 * s;
  const r = 0.15;
  for (const [cx, cy] of [[0.3, 0.3], [0.56, 0.3], [0.43, 0.52], [0.69, 0.52], [0.3, 0.74], [0.56, 0.74]]) {
    c.beginPath();
    for (let i = 0; i <= 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      const x = (cx + Math.cos(a) * r) * s;
      const y = (cy + Math.sin(a) * r) * s;
      if (i) c.lineTo(x, y);
      else c.moveTo(x, y);
    }
    c.stroke();
  }
};

const wire: Painter = (c, s) => {
  c.lineWidth = 0.09 * s;
  c.beginPath();
  c.moveTo(0.06 * s, 0.6 * s);
  c.lineTo(0.28 * s, 0.6 * s);
  c.lineTo(0.4 * s, 0.24 * s);
  c.lineTo(0.56 * s, 0.78 * s);
  c.lineTo(0.68 * s, 0.42 * s);
  c.lineTo(0.94 * s, 0.42 * s);
  c.stroke();
};

const wing: Painter = (c, s) => {
  c.beginPath();
  c.moveTo(0.1 * s, 0.86 * s);
  c.bezierCurveTo(0.2 * s, 0.4 * s, 0.5 * s, 0.14 * s, 0.92 * s, 0.1 * s);
  c.bezierCurveTo(0.8 * s, 0.3 * s, 0.7 * s, 0.34 * s, 0.6 * s, 0.38 * s);
  c.bezierCurveTo(0.7 * s, 0.42 * s, 0.66 * s, 0.5 * s, 0.5 * s, 0.56 * s);
  c.bezierCurveTo(0.56 * s, 0.62 * s, 0.5 * s, 0.68 * s, 0.36 * s, 0.7 * s);
  c.closePath();
  c.fill();
};

const orb: Painter = (c, s) => {
  ring(c, 0.5, 0.5, 0.38, 0.08, s);
  dot(c, 0.5, 0.5, 0.2, s);
};

/** Wide weapon silhouettes (128×64), loosely varied by family. */
function weaponPainter(id: string): Painter {
  return (c, s) => {
    const w = s * 2;
    const y = s * 0.5;
    c.beginPath();
    if (id.includes('scatter')) {
      c.rect(w * 0.08, y - s * 0.14, w * 0.6, s * 0.24);
      c.rect(w * 0.68, y - s * 0.18, w * 0.24, s * 0.3);
    } else if (id.includes('rail')) {
      c.rect(w * 0.04, y - s * 0.1, w * 0.9, s * 0.14);
      c.rect(w * 0.2, y - s * 0.2, w * 0.5, s * 0.1);
    } else if (id.includes('rocket')) {
      c.rect(w * 0.1, y - s * 0.22, w * 0.7, s * 0.36);
    } else if (id.includes('gravity')) {
      c.rect(w * 0.1, y - s * 0.16, w * 0.56, s * 0.3);
      c.arc(w * 0.76, y - s * 0.01, s * 0.22, 0, Math.PI * 2);
    } else if (id.includes('arc')) {
      c.rect(w * 0.08, y - s * 0.12, w * 0.66, s * 0.2);
      c.moveTo(w * 0.74, y - s * 0.22);
      c.lineTo(w * 0.94, y - s * 0.02);
      c.lineTo(w * 0.74, y + s * 0.18);
    } else {
      c.rect(w * 0.08, y - s * 0.12, w * 0.74, s * 0.2);
      c.rect(w * 0.82, y - s * 0.06, w * 0.12, s * 0.08);
    }
    c.fill();
    // Grip + stock.
    poly(c, [[0.36, 0.56], [0.48, 0.56], [0.42, 0.9], [0.3, 0.9]], s);
    c.fillRect(0, y - s * 0.08, w * 0.1, s * 0.22);
  };
}

const PAINTERS: Record<string, Painter> = {
  icon_tag_electric: bolt,
  icon_tag_burn: flame,
  icon_tag_dash: chevrons,
  icon_tag_shield: shield,
  icon_tag_explosive: starburst,
  icon_tag_gravity: vortex,
  icon_tag_kinetic: bullet,
  icon_tag_precision: crosshair,
  icon_tag_heat: heatWaves,
  icon_tag_missile: rocket,
  icon_tag_chain: chainLinks,
  icon_node_start: startRing,
  icon_node_combat: swords,
  icon_node_elite: horned,
  icon_node_loot: gem,
  icon_node_repair: plus,
  icon_node_station: cog,
  icon_node_boss: skull,
  icon_slot_weapon: gun,
  icon_slot_chest: chestPlate,
  icon_slot_shoulders: pauldron,
  icon_slot_back: reactor,
  icon_slot_legs: leg,
  icon_slot_mod: chip,
  icon_slot_mutation: helix,
  icon_mod: chip,
  icon_mutation: helix,
  icon_upgrade: upChevrons,
  icon_scrap: hexNut,
  icon_core: crystal,
  icon_heat: flame,
  icon_lock: padlock,
  icon_sig_arc_dash: bolt,
  icon_sig_siege_field: hexWall,
  icon_sig_phase_echo: echo,
  icon_ult_overload: starburst,
  icon_ult_fortress_protocol: artillery,
  icon_ult_eclipse: crescent,
  icon_passive_live_wire: wire,
  icon_passive_bulwark_frame: shield,
  icon_passive_ghost_drive: wing,
  icon_orb_arsenal: orb,
  icon_orb_armor: orb,
  icon_orb_unstable: orb,
};

function painterFor(key: string): Painter {
  if (key.startsWith('weapon_')) return weaponPainter(key);
  return PAINTERS[key] ?? gem;
}
